import { debounce } from 'ts-debounce';
import { AdminDetailsOutput } from 'dtos/diligence/AdminDetailsOutput';
import { DiligenceOutput } from 'dtos/diligence/DiligenceOutput';
import { ClientOutput, ContactsOutput, InspectionOutput, AddressOutput } from 'dtos/shared/jobDetails';
import { UserSaveInput } from 'dtos/diligence/UserSaveInput';
import { IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx';
import RichTextEditor, { EditorValue } from 'react-rte';
import { AuthStore } from 'stores/users/AuthStore';
import { CancelledEvent, DiligenceAdminStore, SavedEvent } from 'stores/diligence/DiligenceAdminStore';
import { DiligenceFilesStore } from 'stores/diligence/DiligenceFilesStore';
import { saveDelayMs, serverUri } from 'utils/config';
import { HttpException, LoadStatus } from 'utils/types';
import { nullsToUndefined } from 'utils/nullsToUndefined';
import { AddonStore } from 'stores/AddonStore';
import { DiligenceCategoriesStore } from 'stores/shared/DiligenceCategoriesStore';
import { CancelShareEvent, DiligenceAccessStore, ShareAccessEvent } from './DiligenceAccessStore';
import { SharedWithUser } from 'dtos/diligence/DiligenceAccess';

export type JobDetails = {
  client: ClientOutput;
  contacts: ContactsOutput;
  inspection: InspectionOutput;
  address: AddressOutput;
};

export class DiligenceStore {
  readonly events = new EventTarget();
  clientNotes: EditorValue = RichTextEditor.createEmptyValue();
  adminDetails?: AdminDetailsOutput;
  sharedWith?: SharedWithUser[];
  readonly filesStore: DiligenceFilesStore;
  readonly categoriesStore: DiligenceCategoriesStore;
  readonly addonStore?: AddonStore;
  adminStore?: DiligenceAdminStore;
  shareAccessStore?: DiligenceAccessStore;
  saveStatus: LoadStatus = 'none';
  submitAllStatus: LoadStatus = 'none';
  adminDetailsStatus: LoadStatus = 'none';
  showSubmitAllSuccess = false;
  private readonly saveJobDebounced;
  private readonly disposers: IReactionDisposer[] = [];

  constructor(private readonly token: string, public job: DiligenceOutput, private readonly authStore: AuthStore) {
    this.clientNotes = RichTextEditor.createValueFromString(job.clientNotes, 'markdown');

    this.filesStore = new DiligenceFilesStore(token, job.files, authStore);
    this.categoriesStore = new DiligenceCategoriesStore(job.categories);
    this.sharedWith = job.sharedWith || [];

    if (job.accessLevel === 'job') {
      this.addonStore = new AddonStore(token, authStore);
    }

    makeAutoObservable(this, {}, { autoBind: true });
    this.saveJobDebounced = debounce(this.saveJob, saveDelayMs);

    this.disposers.push(
      reaction(
        () => this.authStore.isInternal,
        (isInternal) => {
          if (isInternal) {
            this.loadAdminDetails();
          } else {
            this.adminDetails = undefined;
          }
        }
      )
    );
    if (this.authStore.isInternal) {
      this.loadAdminDetails();
    }
  }

  destroy() {
    this.disposers.forEach((dispose) => dispose());
    this.categoriesStore.destroy();
  }

  activate = async () => {
    await this.addonStore?.activate();
  };

  setClientNotes = (clientNotes: EditorValue) => {
    const hasValueChanged = clientNotes.toString('markdown') !== this.clientNotes.toString('markdown');

    this.clientNotes = clientNotes;
    if (hasValueChanged) {
      this.saveJobDebounced();
    }
  };

  toggleCategory = (title: string) => {
    this.categoriesStore.toggleCategory(title);
    this.saveJobDebounced();
  };

  setCategorySubmitted = (title: string) => {
    this.categoriesStore.setCategorySubmitted(title);
    this.saveJobDebounced();
  };

  setCategoryIncomplete = (title: string) => {
    this.categoriesStore.setCategoryIncomplete(title);
    this.saveJobDebounced();
  };
  setSharedWith(sharedWith: SharedWithUser[]) {
    if (!sharedWith || !Array.isArray(sharedWith)) {
      return;
    }

    this.sharedWith = sharedWith;
  }

  showAdminModal() {
    this.adminStore = new DiligenceAdminStore(
      this.token,
      this.authStore,
      this.job.defaultCategories,
      this.categoriesStore.categories
    );
    this.adminStore.events.addEventListener(SavedEvent.eventType, (event) => {
      if (event instanceof SavedEvent) {
        this.events.dispatchEvent(new SavedEvent(event.categories));
      }
      this.adminStore = undefined;
    });
    this.adminStore.events.addEventListener(CancelledEvent.eventType, () => {
      this.adminStore = undefined;
    });
  }

  showShareModal() {
    this.shareAccessStore = new DiligenceAccessStore(this.token, this.authStore, this.sharedWith, this.setSharedWith);
    this.shareAccessStore.events.addEventListener(ShareAccessEvent.eventType, () => {
      this.events.dispatchEvent(new ShareAccessEvent());
      this.shareAccessStore = undefined;
    });
    this.shareAccessStore.events.addEventListener(CancelShareEvent.eventType, () => {
      this.shareAccessStore = undefined;
    });
  }

  saveJob = async () => {
    this.saveStatus = 'loading';
    const dto: UserSaveInput = {
      clientNotes: this.clientNotes.toString('markdown').trimEnd(),
      categories: this.categoriesStore.categories.map((category) => ({
        title: category.title,
        status: category.status,
      })),
    };
    try {
      const response = await this.authStore.fetchWithAuth(
        `${serverUri}/diligence/${encodeURIComponent(this.token)}/job`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dto),
        }
      );
      if (!response.ok) {
        const exception: HttpException = nullsToUndefined(await response.json());
        throw new Error(exception.message);
      }
      runInAction(() => {
        this.saveStatus = 'success';
      });
    } catch (err) {
      console.error(err);
      runInAction(() => {
        this.saveStatus = 'error';
      });
    }
  };

  loadAdminDetails = async () => {
    this.adminDetailsStatus = 'loading';
    try {
      const response = await this.authStore.fetchWithAuth(
        `${serverUri}/diligence/${encodeURIComponent(this.token)}/admin`
      );
      if (!response.ok) {
        throw new Error();
      }
      const adminDetails: AdminDetailsOutput = nullsToUndefined(await response.json());
      runInAction(() => {
        this.adminDetails = adminDetails;
        this.adminDetailsStatus = 'success';
      });
    } catch (err) {
      console.error(err);
      runInAction(() => {
        this.adminDetailsStatus = 'error';
      });
    }
  };
}
